import { useMemo, useState } from "react";
import SectionHeading from "@/components/base/SectionHeading";
import Tag from "@/components/base/Tag";
import Reveal from "@/components/base/Reveal";
import { feedItems } from "@/mocks/homeData";

const tabs = ["最新", "热议", "实锤"] as const;
type TabKey = (typeof tabs)[number];

const tagVariantMap: Record<string, "primary" | "accent" | "secondary"> = {
  直播事故: "primary",
  数据注水: "accent",
  账号封禁: "secondary",
  粉丝互撕: "primary",
  团队内讧: "secondary",
  言论争议: "accent",
  剪辑造假: "primary",
  联名翻车: "accent",
  深夜致歉: "secondary",
};

export default function LatestFeed() {
  const [activeTab, setActiveTab] = useState<TabKey>("最新");

  const list = useMemo(() => feedItems.filter((item) => item.bucket === activeTab), [activeTab]);

  return (
    <section id="feed" className="bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-[1400px] px-4 md:px-6">
        <SectionHeading
          eyebrow="实时速递"
          eyebrowIcon="ri-flashlight-line"
          title="刚刚更新的现场记录"
          desc="按小时聚合的公开动态，先看最新，再决定往哪个方向往下追。列表越往下越密，细节都在后半段。"
        />

        <div className="grid gap-10 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-8">
            <div className="mb-2 inline-flex items-center gap-1 rounded-full border border-background-200 bg-background-100 p-1">
              {tabs.map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(tab)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                    activeTab === tab
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-600 hover:text-foreground-950"
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            <div>
              {list.map((item, index) => (
                <Reveal key={item.id} delay={index * 60}>
                  <article className="group flex items-start gap-3 border-b border-background-200 py-4 md:gap-5">
                    <span className="mt-0.5 w-12 shrink-0 font-mono text-xs text-secondary-600 md:w-14">
                      {item.time}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="mb-1.5 flex flex-wrap items-center gap-2">
                        <Tag variant={tagVariantMap[item.tag] ?? "secondary"}>{item.tag}</Tag>
                      </div>
                      <a
                        href="#feed"
                        className="block text-sm font-medium leading-6 text-foreground-950 transition-colors group-hover:text-primary-600 md:text-[15px]"
                      >
                        {item.title}
                      </a>
                      <p className="mt-1.5 text-xs leading-relaxed text-foreground-600 md:text-sm">{item.summary}</p>
                      <div className="mt-2 flex items-center gap-4 text-[11px] text-secondary-600 sm:hidden">
                        <span className="flex items-center gap-1">
                          <span className="flex h-3.5 w-3.5 items-center justify-center">
                            <i className="ri-eye-line"></i>
                          </span>
                          {item.views}
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="flex h-3.5 w-3.5 items-center justify-center">
                            <i className="ri-chat-3-line"></i>
                          </span>
                          {item.comments}
                        </span>
                      </div>
                    </div>
                    <div className="hidden shrink-0 items-center gap-4 pt-1 text-xs text-secondary-600 sm:flex">
                      <span className="flex items-center gap-1">
                        <span className="flex h-3.5 w-3.5 items-center justify-center">
                          <i className="ri-eye-line"></i>
                        </span>
                        {item.views}
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="flex h-3.5 w-3.5 items-center justify-center">
                          <i className="ri-chat-3-line"></i>
                        </span>
                        {item.comments}
                      </span>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-base font-bold text-foreground-950">线索热度分布</h3>
                <span className="flex items-center gap-1 text-xs text-secondary-600">
                  <span className="flex h-3.5 w-3.5 items-center justify-center">
                    <i className="ri-time-line"></i>
                  </span>
                  近 24 小时
                </span>
              </div>
              <div className="mt-5 space-y-4">
                {[
                  { label: "直播事故", value: 82 },
                  { label: "粉丝互撕", value: 74 },
                  { label: "数据注水", value: 61 },
                  { label: "账号封禁", value: 43 },
                ].map((row) => (
                  <div key={row.label}>
                    <div className="mb-1.5 flex items-center justify-between text-xs text-foreground-700">
                      <span>{row.label}</span>
                      <span className="font-mono text-secondary-600">{row.value}%</span>
                    </div>
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-background-200">
                      <div
                        className="h-full rounded-full bg-primary-500"
                        style={{ width: `${row.value}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-5 rounded-lg border border-background-200 bg-secondary-50 p-5">
              <h3 className="font-heading text-base font-bold text-foreground-950">今日待核实线索</h3>
              <ul className="mt-4 space-y-3">
                {[
                  "某直播间连麦录音是否经过剪辑",
                  "品牌方下架联名的真实时间点",
                  "矩阵小号是否统一转为仅粉丝可见",
                ].map((text) => (
                  <li key={text} className="flex items-start gap-2 text-xs leading-relaxed text-foreground-700">
                    <span className="mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center text-accent-600">
                      <i className="ri-question-line"></i>
                    </span>
                    {text}
                  </li>
                ))}
              </ul>
              <a
                href="#community"
                className="mt-5 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-secondary-500 px-4 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-secondary-600"
              >
                补充线索
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </span>
              </a>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}